import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import { toJpeg } from 'html-to-image';
import { jsPDF } from "jspdf";
import toast from 'react-hot-toast';

// --- VALIDATION HELPER ---
const checkEmpIdUnique = async (empId: string, currentNurseId?: number) => {
    if (!empId || empId.trim() === '') return true; 
    
    try {
        const token = localStorage.getItem('accessToken');
        const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"; 
        
        let url = `${API_BASE_URL}/api/nurses/check-emp-id?emp_id=${encodeURIComponent(empId)}`;
        if (currentNurseId) url += `&exclude_nurse_id=${currentNurseId}`;
        
        const response = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
        
        if (response.data.exists) {
            toast.error(`Emp ID "${empId}" is already used by ${response.data.name}`);
            return false;
        }
        return true;
    } catch (error) {
        console.error("Failed to validate Emp ID", error);
        toast.error("Could not verify if Emp ID is unique. Check connection.");
        return false;
    }
};
// --- CANVAS UTILITY FUNCTION FOR CROPPING ---
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); 
    image.src = url;
  });

async function getCroppedImg(imageSrc: string, pixelCrop: any): Promise<string | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return canvas.toDataURL('image/jpeg');
}

interface IDCardPreviewProps {
  nurseId: number;
  onClose: () => void;
  onEditProfile: (nurseId: number) => void;
}

const IDCardPreview: React.FC<IDCardPreviewProps> = ({ nurseId, onClose, onEditProfile }) => {
  const [nurse, setNurse] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // --- CROPPER STATE & LOGIC ---
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  // --- CAPTURE & SAVE STATE ---
  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  // --- PREVIEW MODAL STATE ---
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState({ front: '', back: '' });
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Helper: Converts Base64 from html-to-image into a standard File for FastAPI
  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleSaveAndClose = async () => {
    if (!frontCardRef.current || !backCardRef.current) return;
    const isUnique = await checkEmpIdUnique(nurse.emp_id, nurseId);
    if (!isUnique) return; // Stops saving if the ID is taken!
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const headers = { 'Authorization': token ? `Bearer ${token}` : '' };

      // --- PART A: Save the Text & Crop Edits to your new Table ---
      await axios.post(`${API_BASE_URL}/api/nurses/${nurseId}/id-card-details`, {
        id_card_name: nurse.id_name || nurse.full_name,
        id_card_designation: nurse.id_designation || nurse.designation,
        id_card_emp_id: nurse.emp_id,
        id_card_dob: nurse.dob?.split('T')[0], // Clean date format
        id_card_date_of_joining: nurse.date_of_joining?.split('T')[0], // Clean date format
        id_card_blood_group: nurse.id_blood_group || nurse.blood_group,
        id_card_photo_url: croppedImage 
      }, { headers });


      // --- PART B: Generate and upload the S3 JPEGs (Your existing logic) ---
      const frontDataUrl = await toJpeg(frontCardRef.current, { quality: 1.0, pixelRatio: 4 });
      const backDataUrl = await toJpeg(backCardRef.current, { quality: 1.0, pixelRatio: 4 });

      const frontFile = dataURLtoFile(frontDataUrl, 'front.jpg');
      const backFile = dataURLtoFile(backDataUrl, 'back.jpg');

      const formData = new FormData();
      formData.append('front_image', frontFile);
      formData.append('back_image', backFile);

      await axios.post(`${API_BASE_URL}/api/nurses/${nurseId}/save-id-card`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      // Close and refresh
      onClose();
      window.location.reload(); 
      
    } catch (error) {
      console.error("Error saving ID card:", error);
      alert("Failed to save the generated ID card. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- GENERATE ENLARGED PREVIEW ---
  const handleOpenPreview = async () => {
    if (!frontCardRef.current || !backCardRef.current) return;
    setIsGeneratingPreview(true);
    
    try {
      // Create high-res snapshots of the current DOM state
      const frontDataUrl = await toJpeg(frontCardRef.current, { quality: 1.0, pixelRatio: 3 });
      const backDataUrl = await toJpeg(backCardRef.current, { quality: 1.0, pixelRatio: 3 });
      
      setPreviewImages({ front: frontDataUrl, back: backDataUrl });
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Failed to generate preview.");
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    try {
      if (!photoUrl || !croppedAreaPixels) return;
      
      // Force a completely fresh fetch just for the Canvas to bypass the Chrome cache lock
      const canvasSafeUrl = photoUrl + "&corsBypass=" + new Date().getTime();
      
      const croppedImageUrl = await getCroppedImg(canvasSafeUrl, croppedAreaPixels);
      if (croppedImageUrl) {
        setCroppedImage(croppedImageUrl);
        setIsCropperOpen(false);
      }
    } catch (e) {
      console.error(e);
      alert("Could not crop. Ensure your backend allows CORS for images.");
    }
  };

  useEffect(() => {
    const fetchNurseData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        // Fetch master nurse data
        const nurseResponse = await axios.get(`${API_BASE_URL}/api/nurses/${nurseId}`, { headers });
        let combinedData = { ...nurseResponse.data };

        // Attempt to fetch custom ID card overrides
        try {
          const detailsResponse = await axios.get(`${API_BASE_URL}/api/nurses/${nurseId}/id-card-details`, { headers });
          if (detailsResponse.data) {
            const d = detailsResponse.data;
            // Overwrite master data with our saved card edits
            if (d.id_card_name) combinedData.id_name = d.id_card_name;
            if (d.id_card_designation) combinedData.id_designation = d.id_card_designation;
            if (d.id_card_emp_id) combinedData.emp_id = d.id_card_emp_id;
            if (d.id_card_dob) combinedData.dob = d.id_card_dob;
            if (d.id_card_date_of_joining) combinedData.date_of_joining = d.id_card_date_of_joining;
            if (d.id_card_blood_group) combinedData.id_blood_group = d.id_card_blood_group;
            
            // If we saved a cropped image previously, load it!
            if (d.id_card_photo_url) setCroppedImage(d.id_card_photo_url);
          }
        } catch (detailError: any) {
          if (detailError.response?.status !== 404) console.error("Error fetching details:", detailError);
        }

        setNurse(combinedData);
      } catch (error) {
        console.error('Failed to fetch nurse details:', error);
      } finally {
        setLoading(false);
      }
    };
    if (nurseId) fetchNurseData();
  }, [nurseId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNurse((prev: any) => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    // Parse directly from YYYY-MM-DD to avoid UTC→local timezone shift
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3 || parts[0].length !== 4) return dateString;
    const [yyyy, mm, dd] = parts;
    return `${dd}-${mm}-${yyyy}`;
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    const nurseName = nurse?.id_name || nurse?.full_name || 'unknown_nurse';
    const formattedName = nurseName.replace(/\s+/g, '_').toLowerCase();
    document.title = `${formattedName}_idcard`;
    window.print();
    document.title = originalTitle;
  };
  
  // --- NEW DOWNLOAD HELPERS ---
  const forceDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(url, '_blank');
    }
  };

  const downloadAsPDF = async (frontUrl: string, backUrl: string, filename: string) => {
    try {
      const fetchAsBase64 = async (url: string) => {
        const response = await fetch(`${url}?t=${new Date().getTime()}`);
        const blob = await response.blob();
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      };
      const frontBase64 = await fetchAsBase64(frontUrl);
      const backBase64 = await fetchAsBase64(backUrl);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: [2.125, 3.375] });
      pdf.addImage(frontBase64, 'JPEG', 0, 0, 2.125, 3.375);
      pdf.addPage([2.125, 3.375], 'portrait');
      pdf.addImage(backBase64, 'JPEG', 0, 0, 2.125, 3.375);
      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  if (loading || !nurse) {
    return <div style={{ padding: '3rem', textAlign: 'center' }}>Loading card details...</div>;
  }

  const CR80_WIDTH = '2.125in';
  const CR80_HEIGHT = '3.375in';
  
  const styles: { [key: string]: React.CSSProperties } = {
    card: {
      width: CR80_WIDTH,
      height: CR80_HEIGHT,
      backgroundColor: 'white',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Times New Roman", Times, serif'
    },
    inputGroup: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.25rem',
      fontFamily: 'Inter, sans-serif'
    },
    input: {
      padding: '0.5rem',
      borderRadius: '6px',
      border: '1px solid #cbd5e1',
      fontSize: '0.9rem',
      fontFamily: 'Inter, sans-serif'
    }
  };

  // The ?t=${new Date().getTime()} forces the browser to bypass the cache and fetch fresh CORS headers
  const photoUrl = nurse.photo_url ? `${nurse.photo_url}?t=${new Date().getTime()}` : '';
  const finalDisplayPhoto = croppedImage || photoUrl;

  return (
    <div style={{ padding: '1rem' }}>
      {/* --- PRINT CSS (Fixes the 13 pages bug and forces background graphics) --- */}
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #printable-id-cards, #printable-id-cards * { visibility: visible; }
            
            /* The absolute fix for scrollable wrappers (modals) causing extra blank pages */
            html, body, .App, #root {
              height: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
            }
            
            /* Forces all containers to collapse so no empty scrolling space is calculated */
            div {
              overflow: visible !important;
              max-height: none !important;
            }

            .no-print { display: none !important; }
            
            #printable-id-cards {
              position: absolute;
              left: 10px; /* Slight margin from the edge of the paper */
              top: 10px;
              transform: none; /* Remove the centering transform for printing */
              display: flex !important;
              flex-direction: column !important; 
              gap: 20px !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            @page { size: auto; margin: 0mm; }
          }
        `}
      </style>

      {/* Header (Hidden on Print) */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#0f172a', fontFamily: 'Inter, sans-serif' }}>Edit & Preview ID Card</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }} onClick={handlePrint}>
            🖨️ Print Cards
          </button>
          
          {/* --- NEW BUTTON: Save & Close --- */}
          <button 
            onClick={handleSaveAndClose}
            disabled={isSaving}
            style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 600, cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', transition: 'background-color 0.2s' }} 
          >
            {isSaving ? '⏳ Saving...' : '💾 Save & Close'}
          </button>
          
          <button style={{ backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>

      {/* Main Split Layout */}
      <div style={{ display: 'flex', gap: '2rem' }}>
        
        {/* --- LEFT SIDE: LIVE EDIT FORM --- */}
        <div className="no-print" style={{ flex: 1, backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '70vh', overflowY: 'auto' }}>
          <h3 style={{ marginTop: 0, fontSize: '1.1rem', color: '#334155', fontFamily: 'Inter, sans-serif' }}>Live Edit Details</h3>
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1.5rem', fontFamily: 'Inter, sans-serif' }}>Changes here only affect the printed card, not the database.</p>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Name on ID Card</label>
            <input style={styles.input} name="id_name" value={nurse.id_name || nurse.full_name || ''} onChange={handleInputChange} />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Designation on ID</label>
            <input style={styles.input} name="id_designation" value={nurse.id_designation || nurse.designation || ''} onChange={handleInputChange} />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Employee ID</label>
            <input style={styles.input} name="emp_id" value={nurse.emp_id || ''} onChange={handleInputChange} />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Date of Birth (YYYY-MM-DD)</label>
            <input style={styles.input} type="date" name="dob" value={nurse.dob?.split('T')[0] || ''} onChange={handleInputChange} />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Date of Joining (YYYY-MM-DD)</label>
            <input style={styles.input} type="date" name="date_of_joining" value={nurse.date_of_joining?.split('T')[0] || ''} onChange={handleInputChange} />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Blood Group</label>
            <input style={styles.input} name="id_blood_group" value={nurse.id_blood_group || nurse.blood_group || ''} onChange={handleInputChange} />
          </div>
          {/* --- NEW BUTTON: Crop Photo --- */}
          <button 
            onClick={() => setIsCropperOpen(true)}
            disabled={!photoUrl} // Disables if no photo exists
            style={{ width: '100%', marginTop: '0.5rem', padding: '0.65rem', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: 600, cursor: photoUrl ? 'pointer' : 'not-allowed', fontFamily: 'Inter, sans-serif' }}
          >
            ✂️ Crop & Adjust Photo
          </button>
          {/* --- DOWNLOAD SAVED CARDS SECTION --- */}
          {nurse?.id_card_front_url && nurse?.id_card_back_url && (
            <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #cbd5e1' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '0.75rem', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Download Saved Card
              </p>
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  onClick={() => forceDownload(nurse.id_card_front_url, `${nurse.full_name}_idcard_Front.jpg`)}
                  style={{ flex: 1, backgroundColor: 'white', color: '#475569', border: '1px solid #cbd5e1', padding: '0.6rem 0.2rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', transition: 'all 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  ⬇️ Front
                </button>
                
                <button 
                  onClick={() => forceDownload(nurse.id_card_back_url, `${nurse.full_name}_idcard_Back.jpg`)}
                  style={{ flex: 1, backgroundColor: 'white', color: '#475569', border: '1px solid #cbd5e1', padding: '0.6rem 0.2rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', transition: 'all 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  ⬇️ Back
                </button>

                <button 
                  onClick={() => downloadAsPDF(nurse.id_card_front_url, nurse.id_card_back_url, `${nurse.full_name}_ID_Card`)}
                  style={{ flex: 1, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', padding: '0.6rem 0.2rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', transition: 'all 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                >
                  📄 PDF
                </button>
              </div>
            </div>
          )}
        </div>

        {/* --- RIGHT SIDE: VERTICAL CARDS (Updated Pixels and Logic) --- */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '1rem' }}>
          <div id="printable-id-cards" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            
            {/* --- FRONT OF CARD (Print-Safe Version) --- */}
            <div 
            ref={frontCardRef}
            onClick={handleOpenPreview}
            title="Click to enlarge"
            style={{
              ...styles.card,
              cursor: isGeneratingPreview ? 'wait' : 'zoom-in',
              backgroundImage: `url('/assets/idcard_front_empty-2.png')`, 
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none', 
            }}>
              <div style={{ zIndex: 10, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' }}>
                
                {/* Photo: Clickable to trigger Cropper */}
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCropperOpen(true);
                  }}
                  title="Click to adjust and crop photo"
                  style={{ cursor: 'pointer', width: '0.95in', height: '0.95in', borderRadius: '50%', overflow: 'hidden', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', marginTop: '48%', marginBottom: '8px', backgroundColor: '#f1f5f9', flexShrink: 0 }}
                >
                  {finalDisplayPhoto ? (
                    <img src={finalDisplayPhoto} alt="Nurse" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> 
                  ) : (
                    <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.8rem', fontFamily: 'Inter, sans-serif', borderRadius: '50%'}}>No Photo</div>
                  )}
                </div>

                {/* Name & Designation */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '100%', padding: '0 10%' }}>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontFamily: '"Times New Roman", Times, serif', fontWeight: 800, textAlign: 'center', color: '#1e293b', lineHeight: '1.1' }}>
                      {nurse.id_name || nurse.full_name || 'Name Missing'}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontFamily: '"Times New Roman", Times, serif', color: '#0066cc', fontWeight: 700, textAlign: 'center' }}>
                      {
                        (nurse?.id_designation && nurse.id_designation.trim() !== "" && nurse.id_designation !== "null") ? nurse.id_designation : 
                        (nurse?.designation && nurse.designation.trim() !== "" && nurse.designation !== "null") ? nurse.designation : 
                        'Designation Missing'
                      }
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8rem', fontFamily: '"Times New Roman", Times, serif', fontWeight: 700, textAlign: 'center', color: '#333' }}>
                      ID LHHS {nurse.emp_id?.replace(/LHHS\s*/i, '') || '---'}
                    </p>
                </div>
              {/* Vertical ID Text - Bulletproof layout (never shifts when typed) */}
                <div style={{
                  position: 'absolute',
                  left: '8px',  /* Nudge this to move closer/further from the edge */
                  top: '46%',    
                  transform: 'translateY(-50%)', /* Only centers vertically now */
                  writingMode: 'vertical-rl',    /* Natively writes text top-to-bottom */
                  textOrientation: 'mixed',
                  fontSize: '0.55rem',
                  fontFamily: '"Times New Roman", Times, serif',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  color: '#333',
                  whiteSpace: 'nowrap'
                }}>
                  LHHS {nurse.emp_id?.replace(/LHHS\s*/i, '') || '---'}
                </div>

              </div>
            </div>

            {/* --- BACK OF CARD (Using fully erased idcard_back_empty.png Template) --- */}
            <div 
            ref={backCardRef}
            onClick={handleOpenPreview}
            title="Click to enlarge"
            style={{
              ...styles.card,
              cursor: isGeneratingPreview ? 'wait' : 'zoom-in',
              backgroundImage: `url('/assets/idcard_back_empty-2.png')`, 
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              border: 'none', 
            }}>
              
              {/* Back Data Table - Refined Font Weight, Sizing, and Spacing */}
              <div style={{ 
                position: 'absolute', 
                top: '15%',      /* Pushed down slightly to give "Personal Details :" room */
                left: '8.5%',    /* Aligned perfectly with the Instruction text below */
                width: '83%',
                fontFamily: '"Times New Roman", Times, serif', 
                color: '#333'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.62rem' }}>
                  <tbody>
                    <tr>
                      {/* Explicitly set fontWeight to normal so it doesn't look clunky */}
                      <td style={{ paddingBottom: '3px', whiteSpace: 'nowrap', width: '35%', fontWeight: 'normal' }}>Date Of Birth</td>
                      <td style={{ paddingBottom: '3px', width: '5%', textAlign: 'center', fontWeight: 'normal' }}>:</td>
                      {/* .replace is used here to turn "18-May-2001" into "18 May 2001" to match original */}
                      <td style={{ paddingBottom: '3px', paddingLeft: '4px', fontWeight: 'normal' }}>{formatDate(nurse.dob)}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingBottom: '3px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>Date Of Join</td>
                      <td style={{ paddingBottom: '3px', textAlign: 'center', fontWeight: 'normal' }}>:</td>
                      <td style={{ paddingBottom: '3px', paddingLeft: '4px', fontWeight: 'normal' }}>{formatDate(nurse.date_of_joining)}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingBottom: '3px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>Blood Group</td>
                      <td style={{ paddingBottom: '3px', textAlign: 'center', fontWeight: 'normal' }}>:</td>
                      <td style={{ paddingBottom: '3px', paddingLeft: '4px', fontWeight: 'normal' }}>{nurse.id_blood_group || nurse.blood_group || '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

          </div>
        </div>
      </div>
    {/* --- CROPPER MODAL OVERLAY --- */}
      {isCropperOpen && photoUrl && (
        <div className="no-print" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          
          <h3 style={{ color: 'white', fontFamily: 'Inter, sans-serif', marginBottom: '1rem' }}>Pan and Zoom to Crop</h3>
          
          <div style={{ position: 'relative', width: '300px', height: '300px', backgroundColor: '#333', borderRadius: '8px', overflow: 'hidden' }}>
            <Cropper
              image={photoUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          {/* --- ZOOM CONTROLS --- */}
          <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#1e293b', padding: '0.5rem 1.5rem', borderRadius: '8px', border: '1px solid #334155' }}>
            <button 
              onClick={() => setZoom(prev => Math.max(prev - 0.1, 1))}
              style={{ backgroundColor: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: '1.5rem', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#334155'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              title="Zoom Out"
            >
              -
            </button>
            
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{ width: '150px', cursor: 'pointer' }}
            />

            <button 
              onClick={() => setZoom(prev => Math.min(prev + 0.1, 3))}
              style={{ backgroundColor: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', fontSize: '1.2rem', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#334155'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              title="Zoom In"
            >
              +
            </button>
          </div>

          {/* --- ACTION BUTTONS --- */}
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <button onClick={handleSaveCrop} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'background-color 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}>
              Apply Crop
            </button>
            <button onClick={() => setIsCropperOpen(false)} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'background-color 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#475569'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#64748b'; }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* --- ENLARGED PREVIEW MODAL OVERLAY --- */}
      {isPreviewModalOpen && (
        <div 
          className="no-print" 
          onClick={() => setIsPreviewModalOpen(false)} // Close when clicking background
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ marginBottom: '1.5rem', textAlign: 'center', color: 'white', fontFamily: 'Inter, sans-serif' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>High Resolution Preview</h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#cbd5e1', fontSize: '0.9rem' }}>Click anywhere to close</p>
          </div>

          <div 
            onClick={(e) => e.stopPropagation()} // Prevent clicks on the cards from closing the modal
            style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center', padding: '1rem', maxWidth: '100%' }}
          >
            {/* Front Image */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <span style={{ color: '#cbd5e1', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>FRONT</span>
              <img 
                src={previewImages.front} 
                alt="Front ID Card Preview" 
                style={{ height: '500px', width: 'auto', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)' }} 
              />
            </div>
            
            {/* Back Image */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <span style={{ color: '#cbd5e1', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>BACK</span>
              <img 
                src={previewImages.back} 
                alt="Back ID Card Preview" 
                style={{ height: '500px', width: 'auto', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)' }} 
              />
            </div>
          </div>
        </div>
      )}

    </div> // <- This is your existing final closing div
  );
};

export default IDCardPreview;